// UnifiedDashboard.jsx - OPTIMIZED with PAGINATION & INFINITE SCROLL
// Shows ALL data but loads it progressively for performance
// Install: npm install recharts react-gauge-component

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function UnifiedDashboard() {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dateRange, setDateRange] = useState('7days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedRange, setAppliedRange] = useState(null); // {from, to} committed by Go button
  const [excludePending, setExcludePending] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [displayedOrders, setDisplayedOrders] = useState(50); // How many orders to show
  const [allOrdersData, setAllOrdersData] = useState([]); // Store all orders
  
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalCustomers: 0,
    completedOrders: 0,
    inProgressOrders: 0,
    totalProducts: 0,
    inStock: 0,
    outOfStock: 0,
  });

  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  useEffect(() => {
    setDisplayedOrders(50); // Reset to show first 50
    fetchDashboardData();
  }, [dateRange, excludePending, appliedRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    
    try {
      // Get date range. Custom From/To (if both set) overrides the presets.
      let endDate = new Date();
      let startDate = new Date();

      if (appliedRange && appliedRange.from && appliedRange.to) {
        startDate = new Date(appliedRange.from);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(appliedRange.to);
        endDate.setHours(23, 59, 59, 999); // inclusive of the To day
      } else {
        switch(dateRange) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case '7days':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case '30days':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case '90days':
            startDate.setDate(startDate.getDate() - 90);
            break;
        }
      }

      // PARALLEL QUERIES
      let ordersQuery = supabase
        .from('orders')
        .select('id, order_number, user_id, subtotal, total, amount_due, status, created_at, delivery_address_id, delivery_address_snapshot')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });
      
      // Exclude pending orders if enabled
      if (excludePending) {
        ordersQuery = ordersQuery.neq('status', 'pending');
      }

      // Paginate orders to bypass the 1000-row PostgREST cap (revenue/count were undercounting)
      const fetchAllOrders = async () => {
        const PAGE = 1000;
        let all = [];
        let from = 0;
        while (true) {
          let q = supabase
            .from('orders')
            .select('id, order_number, user_id, subtotal, total, amount_due, status, created_at, delivery_address_id, delivery_address_snapshot')
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endDate.toISOString())
            .order('created_at', { ascending: false })
            .range(from, from + PAGE - 1);
          if (excludePending) q = q.neq('status', 'pending');
          const { data, error } = await q;
          if (error) throw error;
          all = all.concat(data || []);
          if (!data || data.length < PAGE) break;
          from += PAGE;
        }
        return all;
      };

      // Product stock counts via exact server-side counts (no row transfer, no cap)
      const [orders, inStockRes, outStockRes, totalProdRes] = await Promise.all([
        fetchAllOrders(),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('in_stock', true),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('in_stock', false),
        supabase.from('products').select('id', { count: 'exact', head: true }),
      ]);

      // Fetch user details for orders
      const userIds = [...new Set(orders.map(o => o.user_id).filter(Boolean))];
      let usersMap = {};
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('user_profiles')
          .select('id, name, phone')
          .in('id', userIds);
        if (usersData) {
          usersData.forEach(u => { usersMap[u.id] = u; });
        }
      }

      // Fetch delivery addresses for orders
      const addressIds = [...new Set(orders.map(o => o.delivery_address_id).filter(Boolean))];
      let addressMap = {};
      if (addressIds.length > 0) {
        const { data: addressesData } = await supabase
          .from('addresses')
          .select('id, physical_address, complex_name, unit_number, area')
          .in('id', addressIds);
        if (addressesData) {
          addressesData.forEach(a => { addressMap[a.id] = a; });
        }
      }

      // Attach user and address info to orders
      orders.forEach(o => {
        o.user = usersMap[o.user_id] || null;
        o.address = o.delivery_address_snapshot || addressMap[o.delivery_address_id] || null;
      });

      // Store ALL orders
      setAllOrdersData(orders);

      // Calculate metrics
      const totalRevenue = orders.reduce((sum, o) => sum + (parseFloat(o.total || o.subtotal) || 0), 0);
      const completedOrders = orders.filter(o => o.status === 'delivered').length;
      const inProgressOrders = orders.filter(o => ['confirmed', 'picking', 'packing'].includes(o.status)).length;
      const uniqueCustomers = new Set(orders.map(o => o.user_id)).size;
      const inStock = inStockRes.count || 0;
      const outOfStock = outStockRes.count || 0;

      setMetrics({
        totalRevenue,
        totalOrders: orders.length,
        totalCustomers: uniqueCustomers,
        completedOrders,
        inProgressOrders,
        totalProducts: totalProdRes.count || 0,
        inStock,
        outOfStock,
      });

      // Sales by date
      const ordersByDate = {};
      orders.forEach(order => {
        const date = new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!ordersByDate[date]) {
          ordersByDate[date] = { date, revenue: 0, orders: 0 };
        }
        ordersByDate[date].revenue += parseFloat(order.total || order.subtotal) || 0;
        ordersByDate[date].orders += 1;
      });
      setSalesData(Object.values(ordersByDate));

      // Recent orders - use displayed limit
      const recentOrdersData = orders.slice(0, displayedOrders).map(o => ({
        id: o.id,
        orderId: o.order_number,
        customer: o.user?.name || 'N/A',
        phone: o.user?.phone || 'N/A',
        address: o.address ? `${o.address.physical_address || ''}${o.address.complex_name ? ', ' + o.address.complex_name : ''}${o.address.unit_number ? ' (' + o.address.unit_number + ')' : ''}${o.address.area ? ' - ' + o.address.area : ''}` : 'N/A',
        amount: parseFloat(o.total || o.subtotal || 0).toFixed(2),
        status: o.status,
        createdAt: new Date(o.created_at).toLocaleString()
      }));
      setRecentOrders(recentOrdersData);

      // Fetch category/product data when needed
      if (activeTab === 'categories' || activeTab === 'overview') {
        const { data: items } = await supabase
          .from('order_items')
          .select('quantity, price, products!inner(category)')
          .in('order_id', orders.slice(0, 100).map(o => o.id));

        if (items) {
          const catRevenue = {};
          items.forEach(item => {
            const cat = item.products?.category || 'Other';
            catRevenue[cat] = (catRevenue[cat] || 0) + (item.price * item.quantity);
          });
          setCategoryData(
            Object.entries(catRevenue)
              .map(([name, value]) => ({ name, value }))
              .sort((a, b) => b.value - a.value)
              .slice(0, 10)
          );
        }
      }

      if (activeTab === 'products') {
        const { data: items } = await supabase
          .from('order_items')
          .select('quantity, price, products!inner(name)')
          .in('order_id', orders.slice(0, 100).map(o => o.id));

        if (items) {
          const prodSales = {};
          items.forEach(item => {
            const name = item.products?.name || 'Unknown';
            if (!prodSales[name]) prodSales[name] = { name, quantity: 0, revenue: 0 };
            prodSales[name].quantity += item.quantity;
            prodSales[name].revenue += item.price * item.quantity;
          });
          setTopProducts(
            Object.values(prodSales)
              .sort((a, b) => b.revenue - a.revenue)
              .slice(0, 20)
          );
        }
      }

    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    
    // Increase displayed orders by 50
    setTimeout(() => {
      setDisplayedOrders(prev => prev + 50);
      
      // Update recent orders display
      const recentOrdersData = allOrdersData.slice(0, displayedOrders + 50).map(o => ({
        id: o.id,
        orderId: o.order_number,
        customer: o.user?.name || 'N/A',
        phone: o.user?.phone || 'N/A',
        address: o.address ? `${o.address.physical_address || ''}${o.address.complex_name ? ', ' + o.address.complex_name : ''}${o.address.unit_number ? ' (' + o.address.unit_number + ')' : ''}${o.address.area ? ' - ' + o.address.area : ''}` : 'N/A',
        amount: parseFloat(o.total || o.subtotal || 0).toFixed(2),
        status: o.status,
        createdAt: new Date(o.created_at).toLocaleString()
      }));
      setRecentOrders(recentOrdersData);
      
      setLoadingMore(false);
    }, 300); // Small delay for UX
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Business overview and metrics</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              disabled={!!appliedRange}
              className={`px-4 py-2 border rounded-lg ${appliedRange ? 'opacity-40' : ''}`}
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>

            {/* Custom From/To range - only applies when you click Go */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              />
              <button
                onClick={() => {
                  if (customFrom && customTo) {
                    setAppliedRange({ from: customFrom, to: customTo });
                  }
                }}
                disabled={!customFrom || !customTo}
                className={`px-4 py-2 rounded-lg text-sm text-white ${customFrom && customTo ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'}`}
              >
                Go
              </button>
              {appliedRange && (
                <button
                  onClick={() => { setCustomFrom(''); setCustomTo(''); setAppliedRange(null); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={excludePending}
                onChange={(e) => setExcludePending(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-600">Exclude Pending</span>
            </label>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-sm text-gray-600 mb-2">Revenue</h3>
            <p className="text-3xl font-bold">R {metrics.totalRevenue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-sm text-gray-600 mb-2">Orders</h3>
            <p className="text-3xl font-bold">{metrics.totalOrders}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-sm text-gray-600 mb-2">Customers</h3>
            <p className="text-3xl font-bold">{metrics.totalCustomers}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-sm text-gray-600 mb-2">Products</h3>
            <p className="text-3xl font-bold">{metrics.totalProducts}</p>
            <p className="text-xs text-gray-500 mt-1">{metrics.inStock} in stock</p>
          </div>
        </div>

        {/* Charts */}
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Sales Trend</h3>
          {salesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#3b82f6" name="Revenue (R)" />
                <Line type="monotone" dataKey="orders" stroke="#10b981" name="Orders" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500">No data</p>
          )}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Recent Orders (Showing {recentOrders.length} of {metrics.totalOrders})
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 text-sm">{order.orderId}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{order.customer}</div>
                      {order.address && order.address !== 'N/A' && (
                        <div className="text-xs text-gray-400 mt-1 max-w-xs truncate" title={order.address}>
                          {order.address}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{order.phone}</td>
                    <td className="px-6 py-4 text-sm">R {order.amount}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{order.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {recentOrders.length < metrics.totalOrders && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    Load More Orders
                    <span className="text-sm opacity-80">
                      ({metrics.totalOrders - recentOrders.length} remaining)
                    </span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* All Orders Loaded Message */}
          {recentOrders.length >= metrics.totalOrders && metrics.totalOrders > 0 && (
            <div className="mt-6 text-center">
              <p className="text-gray-600 font-medium">
                ✓ All {metrics.totalOrders} orders loaded
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
